/**
 * The panes of "Pontaj lunar" and how its URL names them.
 *
 * The page is where everything about the shop's people lives. Three panes read
 * one month — who was in, what it costs, what it was spent building — and the
 * month picker belongs to them alone. The other four stand apart from any
 * month: the employees (their team, program and leave), the crews they make
 * up, the crews' hours report and the external collaborators.
 */
export const TIMESHEET_MONTH_TABS = ['grid', 'payment', 'projects'] as const;
export const TIMESHEET_PEOPLE_TABS = ['employees', 'teams', 'team-hours', 'collaborators'] as const;
export const TIMESHEET_TABS = [...TIMESHEET_MONTH_TABS, ...TIMESHEET_PEOPLE_TABS] as const;
export type TimesheetTab = (typeof TIMESHEET_TABS)[number];

/** The pane the page opens on when the URL names none. */
export const DEFAULT_TIMESHEET_TAB: TimesheetTab = 'grid';

export interface TimesheetSearch {
  /** Absent means the attendance grid. */
  tab?: TimesheetTab;
  /**
   * A team the "Echipe" pane scrolls to and marks — where an unstaffed-team
   * notification on the board sends the manager to staff it.
   */
  team?: string;
}

export function isTimesheetTab(value: unknown): value is TimesheetTab {
  return typeof value === 'string' && (TIMESHEET_TABS as readonly string[]).includes(value);
}

export function isMonthTab(tab: TimesheetTab): boolean {
  return (TIMESHEET_MONTH_TABS as readonly string[]).includes(tab);
}

/**
 * Route search for `/timesheet`. An unknown pane falls back to the grid rather
 * than failing, so an old or mistyped link still lands on the page; the grid
 * itself is left out of the URL, keeping the sidebar's plain link canonical.
 */
export function parseTimesheetSearch(raw: Record<string, unknown>): TimesheetSearch {
  const search: TimesheetSearch = {};
  if (isTimesheetTab(raw.tab) && raw.tab !== DEFAULT_TIMESHEET_TAB) search.tab = raw.tab;
  if (typeof raw.team === 'string' && raw.team.length > 0) search.team = raw.team;
  return search;
}
